/**
 * ================================================================
 * API SERVICE: Document Center (Technical & KYC Forms)
 * ================================================================
 *
 * @service          documentCenterApi
 * @description      API service to fetch and download technical tools and KYC forms
 * @baseUrl          import.meta.env.VITE_DOWNLOAD_CENTER
 *
 * @endpoints
 * - GET  arham_web/v1/fetch-all-document         - List documents by section/sub_section
 * - POST arham_web/v1/fetch-document             - Download a specific document by file_identifier (returns Blob)
 *
 * @authentication  AuthToken header via centralized prepareAuthHeaders
 * @caching         Lists cached briefly; downloads not cached
 *
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer, createBaseQuery, handleApiSuccess } from '@/lib/api/baseQuery';
import type {
  DocumentListItem,
  FetchAllDocumentsParams,
  FetchDocumentPayload,
} from '@/types/downloads';

const baseUrl = import.meta.env.VITE_DOWNLOAD_CENTER as string;

export const documentCenterApi = createApi({
  reducerPath: 'documentCenterApi',
  baseQuery: createBaseQuery(baseUrl),
  tagTypes: ['DocumentCenter'],
  endpoints: (builder) => ({
    /**
     * Fetch all documents by section and sub_section
     * @endpoint GET arham_web/v1/fetch-all-document
     */
    fetchAllDocuments: builder.query<DocumentListItem[], FetchAllDocumentsParams>({
      query: ({ section, sub_section }) => ({
        url: `arham_web/v1/fetch-all-document`,
        params: { section, sub_section },
      }),
      providesTags: (_result, _err, args) => [
        { type: 'DocumentCenter', id: `${args.section}:${args.sub_section}` },
      ],
      keepUnusedDataFor: 120,
      transformResponse: (response: any): DocumentListItem[] =>
        Array.isArray(response) ? response : (response?.data ?? []),
      transformErrorResponse: createApiErrorTransformer('DocumentCenterList', true),
    }),

    /**
     * Fetch a single document content by file_identifier
     * @endpoint POST arham_web/v1/fetch-document
     * @returns Blob - Direct file blob for download
     */
    fetchDocument: builder.mutation<Blob, FetchDocumentPayload>({
      query: (body) => ({
        url: `arham_web/v1/fetch-document`,
        method: 'POST',
        body,
        responseHandler: (response) => response.blob(),
      }),
      transformErrorResponse: createApiErrorTransformer('DocumentCenterDownload', true),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          handleApiSuccess('DocumentCenterDownload', 'Document downloaded.', false);
        } catch {}
      },
    }),
  }),
});

export const {
  useFetchAllDocumentsQuery,
  useLazyFetchAllDocumentsQuery,
  useFetchDocumentMutation,
} = documentCenterApi;

/**
 * ================================================================
 * END OF SERVICE: Document Center
 * ================================================================
 */
